import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "../../../style/slotbooking.css";
import {
  SLOT_MAX_DAYS_AHEAD,
  addDaysYMD,
  buildSlotGroups,
  formatSlotDateLong,
  formatSlotTime,
  getTodayYMD,
  isTimePassedToday,
  parseYMD,
  toYMD,
} from "../../../utils/slotBooking";

interface SlotScheduleProps {
  slotDate: string;
  slotTime: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  onContinue: () => void;
}

const SlotSchedule: React.FC<SlotScheduleProps> = ({
  slotDate,
  slotTime,
  onDateChange,
  onTimeChange,
  onContinue,
}) => {
  const today = getTodayYMD();
  const tomorrow = addDaysYMD(1);
  const groups = useMemo(() => buildSlotGroups(), []);
  const isToday = slotDate === today;
  const minDate = parseYMD(today)!;
  const maxDate = parseYMD(addDaysYMD(SLOT_MAX_DAYS_AHEAD))!;

  const pickDate = (value: string) => {
    onDateChange(value);
    // aaj ka date chuna aur purana time nikal chuka hai -> reset
    if (value === today && slotTime && isTimePassedToday(slotTime)) {
      onTimeChange("");
    }
  };

  const handleContinue = () => {
    if (!slotDate) {
      toast.info("Please select a date");
      return;
    }
    if (!slotTime) {
      toast.info("Please select a time slot");
      return;
    }
    onContinue();
  };

  const ready = !!slotDate && !!slotTime;

  return (
    <div className="slot-card">
      <div className="slot-card-head">
        <div className="slot-head-icon">
          <i className="bi bi-calendar-check"></i>
        </div>
        <div>
          <h5 className="slot-card-title">Schedule your slot</h5>
          <p className="slot-card-sub">
            Pick a date and time for your order. Scan can be uploaded on the scheduled date.
          </p>
        </div>
      </div>

      <div className="row g-4">
        {/* ---------------- DATE ---------------- */}
        <div className="col-lg-6">
          <div className="slot-section-label">
            <span className="slot-step-dot">1</span> Select Date
          </div>

          <div className="slot-quick-row">
            <button
              type="button"
              className={`slot-chip ${slotDate === today ? "active" : ""}`}
              onClick={() => pickDate(today)}
            >
              Today
            </button>
            <button
              type="button"
              className={`slot-chip ${slotDate === tomorrow ? "active" : ""}`}
              onClick={() => pickDate(tomorrow)}
            >
              Tomorrow
            </button>
          </div>

          <div className="slot-calendar">
            <DatePicker
              inline
              selected={parseYMD(slotDate)}
              onChange={(date: Date | null) => date && pickDate(toYMD(date))}
              minDate={minDate}
              maxDate={maxDate}
              calendarStartDay={1}
            />
          </div>
        </div>

        {/* ---------------- TIME ---------------- */}
        <div className="col-lg-6">
          <div className="slot-section-label">
            <span className="slot-step-dot">2</span> Select Time
          </div>

          {!slotDate ? (
            <div className="slot-empty">
              <i className="bi bi-clock-history"></i>
              <p>Select a date to see available time slots</p>
            </div>
          ) : (
            groups.map((group) => (
              <div className="slot-group" key={group.label}>
                <div className="slot-group-title">
                  <i className={`bi ${group.icon}`}></i> {group.label}
                </div>
                <div className="slot-time-grid">
                  {group.times.map((t) => {
                    const disabled = isToday && isTimePassedToday(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={disabled}
                        className={`slot-time-btn ${slotTime === t ? "active" : ""}`}
                        onClick={() => onTimeChange(t)}
                      >
                        {formatSlotTime(t)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ---------------- SUMMARY + CONTINUE ---------------- */}
      <div className={`slot-summary ${ready ? "ready" : ""}`}>
        <div className="slot-summary-info">
          <i className="bi bi-calendar2-event"></i>
          {ready ? (
            <div>
              <strong>
                {formatSlotDateLong(slotDate)} · {formatSlotTime(slotTime)}
              </strong>
              <span>
                {isToday
                  ? "You can upload the scan right after placing the order"
                  : "Scan upload will be enabled on the scheduled date"}
              </span>
            </div>
          ) : (
            <span className="slot-summary-placeholder">No slot selected yet</span>
          )}
        </div>

        <button
          className="slot-continue-btn"
          disabled={!ready}
          onClick={handleContinue}
        >
          Continue <i className="bi bi-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

export default SlotSchedule;
